/**
 * MONSTER TOWN — THE LIGHTING HOOKS. TIME-OF-DAY CREW (src/time/**).
 *
 * Four things get the look applied to them, and that is the whole file:
 *
 *  1. THE SUN. Colour, strength and where it sits, through the two additive
 *     hooks on `GameRenderer` (`setSunLook`, `setSunOffset`). Moving the sun is
 *     what makes a six-o'clock shadow long and an eleven-o'clock one short —
 *     shadows are half of what tells a kid what time it is.
 *  2. THE BOUNCE. `renderer.setAmbience(sky, ground, intensity)` — the existing
 *     call the world crew already makes once at boot. We just keep making it.
 *  3. A WARM FILL nobody else has. One `AmbientLight` we own, at zero all day
 *     and up after dark, so night is COSY rather than merely dark: it lifts the
 *     shadow side of everything by a warm hair, which is the whole difference
 *     between "evening" and "scary".
 *  4. THE BULBS AND THE WINDOWS. Explained at `applyNightMaterials` below.
 *
 * The fifth thing night needs — pulling the WHOLE picture toward one cool blue,
 * which dimming alone does not do — is `tint.ts`, and the note at the top of
 * that file is the evidence for why it had to exist.
 *
 * OTHER CREWS' LIGHTS. The town parents its own warm fill and ambient into the
 * town scene (`src/world/town.ts`), which is right and which would also keep the
 * street in broad daylight at midnight. Rather than edit another crew's file we
 * SCALE what we find: every light hanging off a scene node has its intensity
 * remembered once, and is then multiplied by the look's `sceneLightScale`. The
 * base is captured before the first scale, so a rescan can never ratchet.
 */

import * as THREE from 'three'
import { world } from '../core/scene'
import type { SceneId } from '../core/types'
import { hex } from '../core/palette'
import { flat } from '../render/kit'
import type { GameRenderer } from '../render/renderer'
import { blendHex, sunOffsetFor, type DayLook } from './look'

// ---------------------------------------------------------------------------
// other crews' lights
// ---------------------------------------------------------------------------

interface Tracked {
  readonly light: THREE.Light
  readonly base: number
}

const bases = new Map<string, number>()
let tracked: Tracked[] = []

/**
 * Re-collect every light parented into a scene. Cheap (a handful of nodes, a
 * few dozen objects each) and only run when the world changes shape.
 */
export function rescanSceneLights(): void {
  const found: Tracked[] = []
  for (const id of world.ids()) {
    const node = world.get(id as SceneId)
    if (node === undefined) continue
    node.root.traverse((o) => {
      const l = o as THREE.Light
      if (l.isLight !== true) return
      let base = bases.get(l.uuid)
      if (base === undefined) {
        base = l.intensity
        bases.set(l.uuid, base)
      }
      found.push({ light: l, base })
    })
  }
  tracked = found
}

/** Diagnostics: how many foreign lights the cycle is dimming. */
export function trackedLightCount(): number {
  return tracked.length
}

// ---------------------------------------------------------------------------
// other crews' PAINTED SHADOWS
// ---------------------------------------------------------------------------

/**
 * A SHADOW THAT GLOWS. Critic round 1, and it is worth the whole comment.
 *
 * `src/world/terrain.ts` grounds every scenery prop with a painted contact
 * blob: one shared unlit `MeshBasicMaterial`, colour `shadow` (#5B6E8C, a mid
 * blue-grey at ~46% luminance), normal-blended at 17%. In daylight that is
 * exactly right — it is darker than the grass under it, so it darkens.
 *
 * At 22:00 the grass measures #384c51, about 30% luminance. Composited over
 * that, a 46%-luminance overlay LIFTS the ground. Every tree in the town grew a
 * pale halo after dark (shots/critic-vr1/zoom-night-fountain.png, bright rings
 * at (585,155) and (300,375)). A shadow cannot be brighter than the thing it
 * falls on, at any hour.
 *
 * The right long-term fix is one property in the world crew's own file —
 * `blending: MultiplyBlending` with a pre-lightened colour, which is correct at
 * every hour by construction and needs no clock at all. That is their file, so
 * this crew does what it already does for the window glass and the lamp bulbs:
 * it takes the SHARED MATERIAL INSTANCE and walks its colour down with the sun.
 * One colour write and one opacity write per frame, on one material, for the
 * whole town.
 *
 * FOUND BY IDENTITY, NOT BY NAME. Unlit + transparent + `depthWrite: false` +
 * exactly the palette's `shadow` is the blob and nothing else in the codebase
 * (the build ghost is the only other `shadow`-coloured basic material and it
 * writes depth). Once found, the instance is remembered — we are about to
 * change the colour we matched on.
 */
interface Decal {
  readonly material: THREE.MeshBasicMaterial
  readonly baseOpacity: number
}

const decals: Decal[] = []
const decalSeen = new Set<string>()

/** Night blob colour: the palette's own shadow taken most of the way to ink. */
const BLOB_DAY = hex('shadow')
const BLOB_NIGHT = blendHex(hex('shadow'), hex('ink'), 0.85)
/** It has to work a little harder after dark to read at all. */
const BLOB_NIGHT_GAIN = 1.7

export function rescanNightDecals(): void {
  for (const id of world.ids()) {
    const node = world.get(id as SceneId)
    if (node === undefined) continue
    node.root.traverse((o) => {
      const m = o as THREE.Mesh
      if (m.isMesh !== true) return
      const mat = m.material as THREE.MeshBasicMaterial
      if (mat === undefined || Array.isArray(mat)) return
      if ((mat as THREE.Material).type !== 'MeshBasicMaterial') return
      if (decalSeen.has(mat.uuid)) return
      if (!mat.transparent || mat.depthWrite || mat.blending !== THREE.NormalBlending) return
      if (mat.color.getHex() !== BLOB_DAY) return
      if (mat.opacity > 0.35) return
      decalSeen.add(mat.uuid)
      decals.push({ material: mat, baseOpacity: mat.opacity })
    })
  }
}

let lastDecalNight = -1

/** Walk the painted shadows down with the light. Two writes on one material. */
export function applyNightDecals(night: number): void {
  const n = night < 0 ? 0 : night > 1 ? 1 : night
  if (Math.abs(n - lastDecalNight) < 0.004) return
  lastDecalNight = n
  const colour = n <= 0 ? BLOB_DAY : blendHex(BLOB_DAY, BLOB_NIGHT, n)
  for (const d of decals) {
    d.material.color.setHex(colour)
    d.material.opacity = d.baseOpacity * (1 + (BLOB_NIGHT_GAIN - 1) * n)
  }
}

/** Diagnostics: how many painted-shadow materials the cycle is darkening. */
export function nightDecalCount(): number {
  return decals.length
}

/** Diagnostics: what colour a painted shadow is right now. */
export function nightDecalColor(): { color: number; opacity: number } | null {
  const d = decals[0]
  return d === undefined ? null : { color: d.material.color.getHex(), opacity: d.material.opacity }
}

// ---------------------------------------------------------------------------
// other crews' PAINTED HIGHLIGHTS — the white strip round the plaza
// ---------------------------------------------------------------------------

/**
 * ============ THE WHITE LED STRIP. Critic round 2, blocker. ============
 *
 * *"At night the plaza wall cap is a white LED strip — the brightest thing in
 * the frame, brighter than the lamps."* True, photographed, and it is not the
 * wall. MEASURED with `tools/probe-time-r2b.mjs` (hide one subtree, re-read the
 * same six pixels): the strip is `src/build/plot.ts`'s **plot boundary ring** —
 * `edgeMaterial('flowerWhite', 0.95)`, an unlit `MeshBasicMaterial` laid flat on
 * the paving, which by design STAYS UP IN PLAY MODE because it is what says
 * "this ground is yours". Hide it and the strip is gone from the frame; nothing
 * else moves. `shots/probe-time-r2/22h-base.png` beside `22h-no-bars.png`.
 *
 * WHY IT LOOKS LIKE AN ESCAPE WHEN IT IS NOT ONE. Its renderOrder is 3, well
 * under the multiply pane's 9990, so it DOES take the grade. The numbers at
 * 22:00, on the same pixel:
 *
 *      no grade at all   244,236,219   <- the material's own colour
 *      + multiply        200,191,193   <- the grade, doing its whole job
 *      + lamp pools      255,255,255   <- blown, and clamped
 *      frame mean         77
 *
 * A LIT surface loses ~65% to the night's LIGHTING and then takes the grade on
 * top. An unlit one loses nothing to the lighting, so the grade alone is being
 * asked to carry the entire night for it — and a 0.62 multiply only takes 244 to
 * 200, which is still 2.6x the frame mean before a single lamp touches it. Then
 * the pools (correctly above the grade — a pool of light is a light) saturate it
 * to pure white. The strip is the brightest thing in the town because it is the
 * only thing in the town that never got darker.
 *
 * THE FIX IS THE ONE THIS FILE ALREADY USES TWICE. `src/build/plot.ts` is the
 * build crew's file and crews do not edit each other's; what this crew already
 * does for the world crew's contact blobs above, and for the window glass and
 * lamp bulbs below, is take the SHARED MATERIAL INSTANCE by identity and walk
 * its colour with the sun. One colour write per material per step.
 *
 * FOUND BY IDENTITY, NOT BY NAME: unlit + transparent + `depthWrite:false` +
 * `depthTest:TRUE` + NormalBlending + no texture + a low renderOrder + a BRIGHT
 * colour. That signature is a flat highlight painted on the ground by a crew
 * that has no idea night exists — the ink half of the same ring (0.55 of `ink`)
 * is already dark and is excluded by luminance, the lamp pools are additive, the
 * bulbs are opaque, and the contact blobs are claimed by the scan above and
 * skipped here.
 *
 * THE TWO GUARDS THAT ARE NOT DECORATION. `src/build/ghost.ts` builds a cream
 * pad plate, a green/red footprint and an accent home-marker out of materials
 * that would otherwise match this exactly — and a build affordance dimmed 72%
 * at midnight is a kid unable to see where their piece is about to land. Every
 * one of those is `depthTest: false` (it draws THROUGH the world on purpose)
 * and sits at renderOrder 850+; ground paint is neither. Both are structural
 * properties of "I am a UI overlay", not names, so nothing has to be kept in
 * sync.
 *
 * The base colour is remembered on the way in, because we are about to change
 * what we matched on, and daylight restores it EXACTLY — at `night === 0`
 * nothing here has run.
 */
interface Paint {
  readonly material: THREE.MeshBasicMaterial
  readonly base: number
}

const paints: Paint[] = []

/** Above this a flat painted overlay is bright enough to out-shine the town. */
const PAINT_MIN_LUM = 150
/** Above this it is a UI overlay drawn on top of the world, not paint on it. */
const PAINT_MAX_ORDER = 100
/** How far toward `ink` the brightest overlay paint walks at deepest night. */
const PAINT_NIGHT = 0.72

function lum(c: number): number {
  return 0.2126 * ((c >> 16) & 255) + 0.7152 * ((c >> 8) & 255) + 0.0722 * (c & 255)
}

export function rescanNightPaint(): void {
  for (const id of world.ids()) {
    const node = world.get(id as SceneId)
    if (node === undefined) continue
    node.root.traverse((o) => {
      const m = o as THREE.Mesh
      if (m.isMesh !== true) return
      const mat = m.material as THREE.MeshBasicMaterial
      if (mat === undefined || Array.isArray(mat)) return
      if ((mat as THREE.Material).type !== 'MeshBasicMaterial') return
      if (decalSeen.has(mat.uuid)) return
      if (!mat.transparent || mat.depthWrite || mat.blending !== THREE.NormalBlending) return
      // Draws THROUGH the world, or sits on top of it: a UI overlay (the build
      // ghost), not paint on the ground. See the note above — this is the guard.
      if (!mat.depthTest || m.renderOrder > PAINT_MAX_ORDER) return
      // A textured overlay is somebody's picture, not a flat highlight.
      if (mat.map !== null && mat.map !== undefined) return
      // The grade already handles anything that is genuinely dark.
      if (lum(mat.color.getHex()) < PAINT_MIN_LUM) return
      decalSeen.add(mat.uuid)
      paints.push({ material: mat, base: mat.color.getHex() })
    })
  }
}

const INK = hex('ink')
let lastPaintNight = -1

/** Walk the flat painted highlights down with the light. One write each. */
export function applyNightPaint(night: number): void {
  const n = night < 0 ? 0 : night > 1 ? 1 : night
  if (Math.abs(n - lastPaintNight) < 0.004) return
  lastPaintNight = n
  for (const p of paints) {
    p.material.color.setHex(n <= 0 ? p.base : blendHex(p.base, INK, PAINT_NIGHT * n))
  }
}

/** Diagnostics: how many flat highlights the cycle is darkening, and to what. */
export function nightPaintState(): { count: number; color: number; base: number } {
  const p = paints[0]
  return {
    count: paints.length,
    color: p === undefined ? 0 : p.material.color.getHex(),
    base: p === undefined ? 0 : p.base,
  }
}

// ---------------------------------------------------------------------------
// our own fill
// ---------------------------------------------------------------------------

let fill: THREE.AmbientLight | null = null

export function installLightRig(renderer: GameRenderer): void {
  if (fill !== null) return
  fill = new THREE.AmbientLight(hex('sunWarm'), 0)
  fill.name = 'time:nightFill'
  renderer.scene.add(fill)
}

// ---------------------------------------------------------------------------
// the shared "it is lit" materials
// ---------------------------------------------------------------------------

/**
 * WINDOWS GLOW AND BULBS COME ON, FOR ZERO DRAW CALLS AND ZERO TRAVERSALS.
 *
 * Both are unlit `flat()` materials out of the shared art kit, and `flat()` is
 * CACHED BY COLOUR — so every window pane in the game is one material instance
 * and every lamp bulb in the game is another. `src/world/terrain.ts` already
 * relies on exactly this (it finds the merged bulb mesh by identity against
 * `flat('sunWarm')` and swaps the whole street's lights with one assignment).
 *
 * So "the windows glow warm and the lamp posts light" is two colour writes per
 * frame on two objects. Being unlit is also why it WORKS: while every toon
 * surface in the town is dimming with the sun, these two do not, so they lift
 * out of the dusk on their own — which is what a lit window looks like.
 *
 * `flat('glass', 0.9)` is used by nothing but the building windows in
 * `src/world/buildings.ts`; `flat('sunWarm')` by nothing but lamp and porch
 * bulbs (`world/props.ts`, `content/townProps.ts`, `interior/entrance.ts`).
 * Both are restored to their exact palette value whenever `night` is 0, so at
 * noon this has done nothing at all.
 */
const GLASS_DAY = hex('glass')
const GLASS_NIGHT = blendHex(hex('sunWarm'), hex('uiCoin'), 0.55)
const BULB_DAY = hex('sunWarm')
const BULB_NIGHT = blendHex(hex('uiCoin'), hex('flowerWhite'), 0.4)

let lastNight = -1

export function applyNightMaterials(night: number): void {
  const n = night < 0 ? 0 : night > 1 ? 1 : night
  if (Math.abs(n - lastNight) < 0.004) return
  lastNight = n
  const glass = flat('glass', 0.9) as THREE.MeshBasicMaterial
  const bulb = flat('sunWarm') as THREE.MeshBasicMaterial
  // Windows commit early — a lit window at dusk is the first sign the town is
  // settling down for the night, and it reads long before the sky does.
  const wn = Math.min(1, n * 1.35)
  glass.color.setHex(n <= 0 ? GLASS_DAY : blendHex(GLASS_DAY, GLASS_NIGHT, wn))
  bulb.color.setHex(n <= 0 ? BULB_DAY : blendHex(BULB_DAY, BULB_NIGHT, n))
}

/** Diagnostics: what colour the two shared "it is lit" materials are RIGHT NOW. */
export function nightMaterialColors(): { glass: number; bulb: number } {
  return {
    glass: (flat('glass', 0.9) as THREE.MeshBasicMaterial).color.getHex(),
    bulb: (flat('sunWarm') as THREE.MeshBasicMaterial).color.getHex(),
  }
}

// ---------------------------------------------------------------------------
// the frame
// ---------------------------------------------------------------------------

export function applyLook(renderer: GameRenderer, look: DayLook): void {
  renderer.setSunLook(look.sunColor, look.sunIntensity)
  const o = sunOffsetFor(look)
  renderer.setSunOffset(o.x, o.y, o.z)
  renderer.setAmbience(look.hemiSky, look.hemiGround, look.hemiIntensity)
  if (fill !== null) {
    fill.color.setHex(look.fillColor)
    fill.intensity = look.fillIntensity
  }
  const scale = look.sceneLightScale
  for (const t of tracked) t.light.intensity = t.base * scale
}
